import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { Payment, InsertPayment } from "../../lib/supabase";
import { supabase } from "../../lib/supabase";
import { ENTITY_NAMES } from "../../constants/entities";
import { getPaymentMethods } from "../../utils/payments";
import toast from "react-hot-toast";

interface PaymentFormProps {
  defaultValues?: Payment;
  onSubmit: (data: InsertPayment) => void;
  isLoading: boolean;
}

interface StudentOption {
  id: string;
  codername: string;
  parentname: string;
  email: string;
}

interface RunOption {
  id: string;
  name: string;
  series?: {
    title: string;
  };
}

const PaymentForm: React.FC<PaymentFormProps> = ({
  defaultValues,
  onSubmit,
  isLoading,
}) => {
  const [students, setStudents] = useState<StudentOption[]>([]);
  const [runs, setRuns] = useState<RunOption[]>([]);
  const [isLoadingData, setIsLoadingData] = useState(true);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
  } = useForm<InsertPayment>({
    defaultValues: defaultValues || {
      student_id: "",
      run_id: "",
      amount: 0,
      payment_date: new Date().toISOString().split("T")[0],
      payment_method: "stripe",
      status: "completed",
      notes: "",
    },
  });

  const selectedStudentId = watch("student_id");

  useEffect(() => {
    fetchInitialData();
  }, []);

  // Reset form with new default values when they change (for editing)
  useEffect(() => {
    if (defaultValues) {
      reset(defaultValues);
    }
  }, [defaultValues, reset]);

  useEffect(() => {
    if (selectedStudentId) {
      fetchStudentRuns(selectedStudentId);
    } else {
      setRuns([]);
    }
  }, [selectedStudentId]);

  const fetchInitialData = async () => {
    try {
      setIsLoadingData(true);

      // Fetch students
      const { data: studentsData, error: studentsError } = await supabase
        .from("students")
        .select("id, codername, parentname, email")
        .order("codername");

      if (studentsError) throw studentsError;
      setStudents(studentsData || []);

      // If editing, fetch the student's runs
      if (defaultValues?.student_id) {
        await fetchStudentRuns(defaultValues.student_id);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load form data");
    } finally {
      setIsLoadingData(false);
    }
  };

  const fetchStudentRuns = async (studentId: string) => {
    try {
      // Get the student's current run
      const { data: studentData, error: studentError } = await supabase
        .from("students")
        .select("run_id")
        .eq("id", studentId)
        .single();

      if (studentError) throw studentError;

      // Fetch all runs, but prioritize the student's current run
      const { data: runsData, error: runsError } = await supabase
        .from("runs")
        .select(
          `
          *,
          series:series_id (
            title
          )
        `
        )
        .eq("active", true)
        .order("name");

      if (runsError) throw runsError;

      // Sort runs to put the student's current run first
      const sortedRuns = (runsData || []).sort((a, b) => {
        if (a.id === studentData?.run_id) return -1;
        if (b.id === studentData?.run_id) return 1;
        return 0;
      });

      setRuns(sortedRuns);

      // Auto-select the student's current run if editing a new payment
      if (!defaultValues && studentData?.run_id) {
        setValue("run_id", studentData.run_id);
      }
    } catch (error) {
      console.error("Error fetching student runs:", error);
      toast.error("Failed to load student's groups");
    }
  };

  const paymentMethods = getPaymentMethods();

  const formatStudentOption = (student: StudentOption) => {
    return `${student.codername} (${student.parentname})`;
  };

  const formatRunOption = (run: RunOption) => {
    const seriesInfo = run.series ? ` - ${run.series.title}` : "";
    return `${run.name}${seriesInfo}`;
  };

  if (isLoadingData) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Student Selection */}
        <div>
          <label htmlFor="student_id" className="label">
            Student *
          </label>
          <select
            {...register("student_id", { required: "Student is required" })}
            className="input"
          >
            <option value="">Select a student</option>
            {students.map((student) => (
              <option key={student.id} value={student.id}>
                {formatStudentOption(student)}
              </option>
            ))}
          </select>
          {errors.student_id && (
            <p className="mt-1 text-sm text-error-500">
              {errors.student_id.message}
            </p>
          )}
        </div>

        {/* Run Selection */}
        <div>
          <label htmlFor="run_id" className="label">
            Group
          </label>
          <select
            {...register("run_id")}
            className="input"
            disabled={!selectedStudentId}
          >
            <option value="">Select a group (optional)</option>
            {runs.map((run) => (
              <option key={run.id} value={run.id}>
                {formatRunOption(run)}
              </option>
            ))}
          </select>
          {!selectedStudentId && (
            <p className="mt-1 text-sm text-neutral-500">
              Select a student first to see available groups
            </p>
          )}
        </div>

        {/* Amount */}
        <div>
          <label htmlFor="amount" className="label">
            Amount *
          </label>
          <input
            type="number"
            step="0.01"
            min="0"
            {...register("amount", {
              required: "Amount is required",
              min: { value: 0, message: "Amount must be positive" },
            })}
            className="input"
            placeholder="0.00"
          />
          {errors.amount && (
            <p className="mt-1 text-sm text-error-500">
              {errors.amount.message}
            </p>
          )}
        </div>

        {/* Payment Date */}
        <div>
          <label htmlFor="payment_date" className="label">
            Payment Date *
          </label>
          <input
            type="date"
            {...register("payment_date", {
              required: "Payment date is required",
            })}
            className="input"
          />
          {errors.payment_date && (
            <p className="mt-1 text-sm text-error-500">
              {errors.payment_date.message}
            </p>
          )}
        </div>

        {/* Payment Method */}
        <div>
          <label htmlFor="payment_method" className="label">
            Payment Method
          </label>
          <select {...register("payment_method")} className="input">
            {paymentMethods.map((method) => (
              <option key={method.value} value={method.value}>
                {method.label}
              </option>
            ))}
          </select>
        </div>

        {/* Payment Status */}
        <div>
          <label htmlFor="status" className="label">
            Status *
          </label>
          <select
            {...register("status", { required: "Status is required" })}
            className="input"
          >
            <option value="not_received">Not Received</option>
            <option value="pending">Pending</option>
            <option value="partial">Partial Payment</option>
            <option value="completed">Completed</option>
            <option value="failed">Failed</option>
            <option value="cancelled">Cancelled</option>
            <option value="refunded">Refunded</option>
          </select>
          {errors.status && (
            <p className="mt-1 text-sm text-error-500">
              {errors.status.message}
            </p>
          )}
        </div>
      </div>

      {/* Notes */}
      <div>
        <label htmlFor="notes" className="label">
          Notes
        </label>
        <textarea
          {...register("notes")}
          rows={3}
          className="input"
          placeholder="Optional notes about this payment..."
        />
        <p className="mt-1 text-sm text-neutral-500">
          Add any additional information about this payment.
        </p>
      </div>

      {/* Submit Button */}
      <div className="flex justify-end">
        <button
          type="submit"
          disabled={isLoading}
          className="btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Saving...
            </>
          ) : (
            `${defaultValues ? "Update" : "Create"} ${ENTITY_NAMES.PAYMENT}`
          )}
        </button>
      </div>
    </form>
  );
};

export default PaymentForm;
